import { useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  ScriptableContext,
} from "chart.js";
import type { UserToolboxCheckinI } from "../../../types";
import {
  parseHex,
  toRgb,
  toRgba,
  ratingShade,
} from "../../../utils/topicBranding";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

interface CheckInRatingChartProps {
  checkins: UserToolboxCheckinI[];
  topicColor?: string;
  topicTitle?: string;
  ratingLabel?: string;
}

const FALLBACK_COLOR = "#7c5d9e";

export default function CheckInRatingChart({
  checkins,
  topicColor,
  topicTitle,
  ratingLabel = "Rating",
}: CheckInRatingChartProps) {
  const baseRgb = useMemo(
    () => parseHex(topicColor ?? FALLBACK_COLOR) ?? parseHex(FALLBACK_COLOR)!,
    [topicColor],
  );
  const lineColor = toRgb(baseRgb);
  const fillColor = toRgba(baseRgb, 0.15);

  const ratedCheckins = useMemo(
    () =>
      checkins.filter(
        (c) => c.rating !== undefined && c.ratingScale !== undefined,
      ),
    [checkins],
  );

  const sorted = useMemo(
    () =>
      [...ratedCheckins].sort(
        (a, b) =>
          new Date(a.createdAt ?? 0).getTime() -
          new Date(b.createdAt ?? 0).getTime(),
      ),
    [ratedCheckins],
  );

  const scale = sorted.length > 0 ? (sorted[0].ratingScale ?? 5) : 5;

  const { labels, ratings } = useMemo(() => {
    const labels: string[] = [];
    const ratings: (number | null)[] = [];

    for (const ci of sorted) {
      labels.push(
        new Date(ci.createdAt ?? 0).toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
        }),
      );
      ratings.push(ci.rating ?? null);
    }

    return { labels, ratings };
  }, [sorted]);

  const yMax = useMemo(() => {
    const maxRating = Math.max(
      ...ratings.filter((r): r is number => r !== null),
    );
    return isFinite(maxRating) ? maxRating + 1 : scale;
  }, [ratings, scale]);

  const options: ChartOptions<"line"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          mode: "index" as const,
          intersect: false,
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          titleFont: { size: 14 },
          bodyFont: { size: 13 },
          padding: 12,
          cornerRadius: 8,
          callbacks: {
            label(ctx) {
              const val = ctx.parsed.y;
              return `${ratingLabel}: ${val}/${scale}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            maxRotation: 45,
            minRotation: 0,
            autoSkip: true,
            maxTicksLimit: 10,
          },
        },
        y: {
          type: "linear" as const,
          min: 0,
          max: yMax,
          title: {
            display: true,
            text: ratingLabel,
            font: { size: 12, weight: "bold" as const },
          },
          grid: { color: "rgba(0, 0, 0, 0.06)" },
          ticks: {
            stepSize: 1,
            precision: 0,
            callback: (value) => (value === yMax ? null : value),
          },
        },
      },
      interaction: {
        mode: "nearest" as const,
        axis: "x" as const,
        intersect: false,
      },
    }),
    [ratingLabel, scale, yMax],
  );

  const chartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: ratingLabel,
          data: ratings,
          borderColor: lineColor,
          backgroundColor: fillColor,
          fill: true,
          tension: 0.3,
          pointRadius: 5,
          pointHoverRadius: 7,
          pointBackgroundColor: (ctx: ScriptableContext<"line">) => {
            const val = ctx.parsed?.y;
            return val != null
              ? ratingShade(val, scale, topicColor)
              : lineColor;
          },
          pointBorderColor: "white",
          pointBorderWidth: 2,
          borderWidth: 2.5,
          spanGaps: false,
        },
      ],
    }),
    [labels, ratings, ratingLabel, scale, lineColor, fillColor, topicColor],
  );

  if (sorted.length < 2) {
    return (
      <div className="flex-1 flex items-center justify-center rounded-lg bg-gray-50 border border-dashed border-gray-200 min-h-[200px]">
        <p className="text-sm text-gray-400">
          Complete more check-ins to see trends.
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col justify-center rounded-lg bg-gray-50 border border-gray-200 p-4 min-h-[200px]">
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">
        {topicTitle ? `${topicTitle} Trend` : "Rating Trend"}
      </p>
      <div style={{ height: "180px", width: "100%" }}>
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
}
